import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  KeyRound,
  LockKeyhole,
  ShieldCheck
} from "lucide-react";
import { Button, FieldError } from "../components/ui";
import { userErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fieldErrorId, useFormErrors } from "../lib/form-errors";

export function ChangePasswordPage() {
  const { changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const formErrors = useFormErrors();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    formErrors.clear();
    try {
      await changePassword({ currentPassword, newPassword });
      await navigate({ to: "/inbox" });
    } catch (cause) {
      if (!formErrors.capture(cause)) {
        setError(userErrorMessage(cause, "Não foi possível atualizar a senha."));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-page password-change-page">
      <section className="login-showcase password-showcase">
        <div className="login-brand">
          <span className="brand-mark">
            <ShieldCheck size={25} strokeWidth={2.4} />
          </span>
          <span>
            <strong>HTZAP</strong>
            <small>OFICIAL</small>
          </span>
        </div>
        <div className="showcase-copy">
          <span className="showcase-chip">
            <KeyRound size={15} /> Primeiro acesso protegido
          </span>
          <h1>
            Defina sua senha
            <em> pessoal e intransferível.</em>
          </h1>
          <p>
            A senha inicial é temporária. Ao trocá-la, as demais sessões serão
            revogadas e a alteração ficará registrada na auditoria.
          </p>
          <ul>
            <li>
              <CheckCircle2 size={15} /> Mínimo de 12 caracteres
            </li>
            <li>
              <CheckCircle2 size={15} /> Sessão rotacionada após a troca
            </li>
            <li>
              <CheckCircle2 size={15} /> Credenciais nunca salvas no navegador
            </li>
          </ul>
        </div>
      </section>

      <section className="login-form-area">
        <form className="login-form" onSubmit={submit}>
          <span className="login-lock">
            <LockKeyhole size={22} />
          </span>
          <span className="eyebrow">Segurança da conta</span>
          <h2>Troque a senha inicial</h2>
          <p>Você poderá acessar o workspace depois desta etapa.</p>
          <label className="field">
            <span>Senha atual</span>
            <input
              name="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(event) => { setCurrentPassword(event.target.value); formErrors.clear("currentPassword"); }}
              aria-invalid={Boolean(formErrors.errors.currentPassword)}
              aria-describedby={formErrors.errors.currentPassword ? fieldErrorId("currentPassword") : undefined}
              autoComplete="current-password"
              required
            />
            <FieldError field="currentPassword" message={formErrors.errors.currentPassword} />
          </label>
          <label className="field">
            <span>Nova senha</span>
            <input
              name="newPassword"
              type="password"
              value={newPassword}
              onChange={(event) => { setNewPassword(event.target.value); formErrors.clear("newPassword"); }}
              aria-invalid={Boolean(formErrors.errors.newPassword)}
              aria-describedby={formErrors.errors.newPassword ? fieldErrorId("newPassword") : undefined}
              autoComplete="new-password"
              minLength={12}
              required
            />
            <FieldError field="newPassword" message={formErrors.errors.newPassword} />
          </label>
          <label className="field">
            <span>Confirmar nova senha</span>
            <input
              name="confirmation"
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
              aria-invalid={Boolean(confirmation && newPassword !== confirmation)}
              aria-describedby={confirmation && newPassword !== confirmation ? fieldErrorId("confirmation") : undefined}
            />
            <FieldError field="confirmation" message={confirmation && newPassword !== confirmation ? "A confirmação não corresponde à nova senha." : undefined} />
          </label>
          {error ? <div className="form-error">{error}</div> : null}
          <Button
            type="submit"
            disabled={
              loading ||
              !currentPassword ||
              newPassword.length < 12 ||
              newPassword !== confirmation
            }
            className="login-submit"
          >
            {loading ? "Protegendo conta..." : "Salvar nova senha"}
            <ArrowRight size={18} />
          </Button>
          <button
            type="button"
            className="login-secondary-action"
            onClick={() => void logout()}
          >
            Sair e continuar depois
          </button>
        </form>
      </section>
    </main>
  );
}
